import { memo, useCallback, useState } from 'react';

import { Check, Close } from '@air/next-icons';
import { Chip } from '@air/primitive-chip';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputTrigger,
  type ComboboxInputTriggerProps,
  ComboboxItem,
  ComboboxList,
  ComboboxLoading,
} from '@air/primitive-combobox';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { type SmartTagBase, type SmartTagSelectOption } from '~/components/SmartTagSelect/utils';

export type SmartTagsSelectProps = Pick<ComboboxInputTriggerProps, 'className'> & {
  isLoading?: boolean;
  onInputChange: (value: string) => void;
  onSmartTagsSelectionChange: (newSmartTags: SmartTagSelectOption[]) => void;
  placeholder?: string;
  selectedSmartTags: SmartTagSelectOption[];
  smartTags: SmartTagBase[];
};

export const SmartTagsSelect = memo(
  ({
    className,
    isLoading = false,
    onInputChange,
    onSmartTagsSelectionChange,
    placeholder = 'Select smart tags...',
    selectedSmartTags = [],
    smartTags,
  }: SmartTagsSelectProps) => {
    const [searchValue, setSearchValue] = useState('');

    const onRemoveSmartTag = useCallback(
      (smartTag: SmartTagSelectOption) => {
        onSmartTagsSelectionChange(selectedSmartTags.filter((tag) => tag.value !== smartTag.value));
      },
      [onSmartTagsSelectionChange, selectedSmartTags],
    );

    const onSmartTagClick = useCallback(
      (smartTag: string) => {
        if (selectedSmartTags.some((tag) => tag.label === smartTag)) {
          return onRemoveSmartTag({ value: smartTag, label: smartTag });
        } else {
          onSmartTagsSelectionChange([
            ...selectedSmartTags,
            {
              value: smartTag,
              label: smartTag,
            },
          ]);
        }
      },
      [onRemoveSmartTag, onSmartTagsSelectionChange, selectedSmartTags],
    );

    const onValueChange = useCallback(
      (value: string) => {
        setSearchValue(value);
        onInputChange(value);
      },
      [onInputChange],
    );

    return (
      <Combobox>
        <ComboboxInputTrigger className={className} data-id="smart-tag" size="large">
          {selectedSmartTags.length > 0 ? (
            selectedSmartTags.map((smartTag) => (
              <Chip data-id={smartTag.value} key={smartTag.value} shape="rounded" size="medium">
                <span className="truncate">{smartTag.label}</span>
                <IconButton
                  appearance="ghost"
                  color="grey"
                  icon={Close}
                  label="Remove smart tag"
                  onClick={(event) => {
                    event.stopPropagation();

                    onRemoveSmartTag(smartTag);
                  }}
                  size="extra-small"
                />
              </Chip>
            ))
          ) : (
            <div className="text-grey-10">{placeholder}</div>
          )}
        </ComboboxInputTrigger>
        <ComboboxContent
          className="max-h-[min(320px,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)]"
          sideOffset={4}
          shouldFilter={false}
        >
          <ComboboxInput onValueChange={onValueChange} placeholder="Search smart tags..." value={searchValue} />

          {isLoading ? (
            <ComboboxLoading>Loading...</ComboboxLoading>
          ) : (
            <ComboboxEmpty>No smart tags found.</ComboboxEmpty>
          )}

          <ComboboxList>
            {smartTags.map((smartTag) => {
              const isSelected = selectedSmartTags.some((tag) => tag.label === smartTag.label);

              return (
                <ComboboxItem data-id={smartTag.id} onSelect={() => onSmartTagClick(smartTag.id)} key={smartTag.id}>
                  {isSelected ? <Check className="size-4 shrink-0 text-blue-9" /> : <div className="size-4 shrink-0" />}
                  <div className={cn('truncate', isSelected ? 'opacity-70' : '')}>{smartTag.label}</div>
                </ComboboxItem>
              );
            })}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  },
);

SmartTagsSelect.displayName = 'SmartTagsSelect';
